// File: src/MathTools/NonLinearSystemTool.js
import React, { useState } from "react";
import CoordinatePlane from "./CoordinatePlane";

/**
 * A minimal tool for 2‐equation systems:
 * line, parabola, circle, plus added “exponential” & “logarithm” options (for plotting).
 * 
 * We only solve line/para/circle combos analytically. 
 * Exponential/log combos will produce no solutions in solveSystem (unless you add numeric code).
 */

//----------------- Solve the system (line, parabola, circle combos) ----------------------//
function solveSystem(e1, e2) {
  if (e1.type==='line' && e2.type==='line') {
    return solveLineLine(e1.params, e2.params);
  }
  else if (e1.type==='line' && e2.type==='parabola') {
    return solveLineParabola(e1.params, e2.params);
  }
  else if (e1.type==='parabola' && e2.type==='line') {
    return solveLineParabola(e2.params, e1.params);
  }
  else if (e1.type==='line' && e2.type==='circle') {
    return solveLineCircle(e1.params, e2.params);
  }
  else if (e1.type==='circle' && e2.type==='line') {
    return solveLineCircle(e2.params, e1.params);
  }
  else if (e1.type==='parabola' && e2.type==='parabola') {
    return solveParabolaParabola(e1.params, e2.params);
  }
  // Exponential/log => not handled analytically here
  return [];
}

// Solve line‐line: eqn => y=a1*x+b1, eqn => y=a2*x+b2
function solveLineLine(p1, p2) {
  const denom = (p1.a - p2.a);
  if (Math.abs(denom) < 1e-9) {
    // parallel or same line => no distinct intersections
    return [];
  }
  const x = (p2.b - p1.b)/denom;
  const y = p1.a*x + p1.b;
  return [{ x, y }];
}

// Solve line‐parabola: line => y=aL*x+bL; parabola => y=aP*x^2+bP*x+cP
function solveLineParabola(line, parabola) {
  // set line = parabola => aL*x + bL = aP*x^2 + bP*x + cP
  // => aP*x^2 + (bP - aL)*x + (cP - bL)=0
  const A = parabola.a;
  const B = parabola.b - line.a;
  const C = parabola.c - line.b;
  const xs = solveQuadratic(A, B, C);
  return xs.map(x => {
    const y = line.a*x + line.b;
    return { x, y };
  });
}

// Solve line‐circle: line => y=aL*x+bL; circle => (x-h)^2 + (y-k)^2 = r^2
function solveLineCircle(line, circle) {
  // (1+aL^2)x^2 + [2aL(bL - k) -2h]x + [h^2+(bL-k)^2 -r^2]=0
  const A_ = 1 + line.a*line.a;
  const B_ = 2*line.a*(line.b - circle.k) - 2*circle.h;
  const C_ = circle.h*circle.h + (line.b - circle.k)*(line.b - circle.k) - circle.r*circle.r;
  const xs = solveQuadratic(A_, B_, C_);
  return xs.map(x => {
    const y = line.a*x + line.b;
    return { x, y };
  });
}

// Solve parabola‐parabola: a1x^2+b1x+c1 = a2x^2+b2x+c2
function solveParabolaParabola(p1, p2) {
  const A = p1.a - p2.a;
  const B = p1.b - p2.b;
  const C = p1.c - p2.c;
  const xs = solveQuadratic(A, B, C);
  return xs.map(x => {
    const y = p1.a*x*x + p1.b*x + p1.c;
    return { x, y };
  });
}

// Solve Ax^2+Bx+C=0 => array of real roots
function solveQuadratic(A,B,C) {
  if (Math.abs(A)<1e-9) {
    // degrade to linear
    if (Math.abs(B)<1e-9) return [];
    return [-C/B];
  }
  const disc = B*B - 4*A*C;
  if (disc<0) return [];
  if (Math.abs(disc)<1e-9) {
    return [-B/(2*A)];
  }
  const sqrtD = Math.sqrt(disc);
  const x1 = (-B - sqrtD)/(2*A);
  const x2 = (-B + sqrtD)/(2*A);
  return x1<x2 ? [x1,x2] : [x2,x1];
}


//--------------------- MAIN COMPONENT ----------------------------//
export default function NonLinearSystemTool() {
  const [eqn1Type, setEqn1Type] = useState("line");
  const [eqn2Type, setEqn2Type] = useState("parabola");

  // We'll store parameters for each equation type:
  // line => {a,b}
  // parabola => {a,b,c}
  // circle => {h,k,r}
  // exponential => {a,b,c}
  // logarithm => {base}
  const [eqn1Params, setEqn1Params] = useState({ a:1, b:0, c:0, h:0, k:0, r:4, base:2 });
  const [eqn2Params, setEqn2Params] = useState({ a:1, b:-3, c:4, h:0, k:0, r:4, base:2 });

  const [solutions, setSolutions] = useState([]);

  function handleSolve() {
    const e1 = { type: eqn1Type, params: eqn1Params };
    const e2 = { type: eqn2Type, params: eqn2Params };
    const sol = solveSystem(e1, e2);
    setSolutions(sol);
  }

  // Build up line segments for each eqn => pass to <CoordinatePlane lines={...}/>
  const linesToDraw = [
    ...getEquationSegments(eqn1Type, eqn1Params),
    ...getEquationSegments(eqn2Type, eqn2Params)
  ];

  return (
    <div style={{ display: "flex", gap: "2rem" }}>
      <div style={{ flex: 1 }}>
        <h3>Equation 1</h3>
        <select className="dropdown1" value={eqn1Type} onChange={e => setEqn1Type(e.target.value)}>
          <option value="line">Line (y=ax+b)</option>
          <option value="parabola">Parabola (y=ax²+bx+c)</option>
          <option value="circle">Circle ((x-h)²+(y-k)²=r²)</option>
          <option value="exponential">Exponential (y=a·b^x + c)</option>
          <option value="logarithm">Logarithmic (y=log_b(x))</option>
        </select>
        <EquationParams eqType={eqn1Type} params={eqn1Params} onChange={setEqn1Params} />

        <h3>Equation 2</h3>
        <select className="dropdown1" value={eqn2Type} onChange={e => setEqn2Type(e.target.value)}>
          <option value="line">Line (y=ax+b)</option>
          <option value="parabola">Parabola (y=ax²+bx+c)</option>
          <option value="circle">Circle ((x-h)²+(y-k)²=r²)</option>
          <option value="exponential">Exponential (y=a·b^x + c)</option>
          <option value="logarithm">Logarithmic (y=log_b(x))</option>
        </select>
        <EquationParams eqType={eqn2Type} params={eqn2Params} onChange={setEqn2Params} />

        <button className="button1" onClick={handleSolve} style={{ marginTop: "1em" }}>
          Solve System
        </button>

        <div style={{ marginTop:"1em" }}>
          <strong>Solutions:</strong>
          {solutions.length === 0 && <p>No real intersection or not solved yet (or numeric approach needed).</p>}
          {solutions.map((pt,i) => (
            <p key={i}>x={pt.x.toFixed(3)}, y={pt.y.toFixed(3)}</p>
          ))}
        </div>
      </div>

      <div style={{ flex:"0 0 auto" }}>
        <CoordinatePlane
          width={300}
          height={300}
          showAxes
          disablePlot
          lines={linesToDraw}
          extraPoints={solutions.map(s => ({ x:s.x, y:s.y }))}
        />
      </div>
    </div>
  );
}


//--------------------- Parameter Inputs per eqType -------------------------//
function EquationParams({ eqType, params, onChange }) {
  switch(eqType) {
    case "line":
      return (
        <div>
          <label>a: <input
            type="number"
            className="inputBox"
            value={params.a}
            onChange={e=>onChange({...params, a:+e.target.value})}
          /></label>
          <label>b: <input
            type="number"
            className="inputBox"
            value={params.b}
            onChange={e=>onChange({...params, b:+e.target.value})}
          /></label>
        </div>
      );
    case "parabola":
      return (
        <div>
          <label>a: <input
            type="number"
            className="inputBox"
            value={params.a}
            onChange={e=>onChange({...params, a:+e.target.value})}
          /></label>
          <label>b: <input
            type="number"
            className="inputBox"
            value={params.b}
            onChange={e=>onChange({...params, b:+e.target.value})}
          /></label>
          <label>c: <input
            type="number"
            className="inputBox"
            value={params.c}
            onChange={e=>onChange({...params, c:+e.target.value})}
          /></label>
        </div>
      );
    case "circle":
      return (
        <div>
          <label>h: <input
            type="number"
            className="inputBox"
            value={params.h}
            onChange={e=>onChange({...params, h:+e.target.value})}
          /></label>
          <label>k: <input
            type="number"
            className="inputBox"
            value={params.k}
            onChange={e=>onChange({...params, k:+e.target.value})}
          /></label>
          <label>r: <input
            type="number"
            className="inputBox"
            value={params.r}
            onChange={e=>onChange({...params, r:+e.target.value})}
          /></label>
        </div>
      );
    case "exponential":
      return (
        <div>
          <label>a: <input
            type="number"
            className="inputBox"
            value={params.a}
            onChange={e=>onChange({...params, a:+e.target.value})}
          /></label>
          <label>b: <input
            type="number"
            className="inputBox"
            value={params.b}
            onChange={e=>onChange({...params, b:+e.target.value})}
          /></label>
          <label>c: <input
            type="number"
            className="inputBox"
            value={params.c}
            onChange={e=>onChange({...params, c:+e.target.value})}
          /></label>
          <p>y = a * (b^x) + c</p>
        </div>
      );
    case "logarithm":
      return (
        <div>
          <label>base: <input
            type="number"
            className="inputBox"
            value={params.base}
            onChange={e=>onChange({...params, base:+e.target.value})}
          /></label>
          <p>y = log_base(x)</p>
        </div>
      );
    default:
      return <p>Unknown type.</p>;
  }
}


//--------------------- Plot each eqn as line segments  --------------------//
function getEquationSegments(type, p) {
  const segs = [];

  switch(type) {
    case "line": {
      // One segment from x=-10..10
      const y1 = p.a*(-10)+p.b;
      const y2 = p.a*(10) + p.b;
      segs.push({
        start: [-10, y1],
        end:   [ 10, y2],
        color: "red"
      });
      break;
    }
    case "parabola": {
      const step=0.4;
      for (let x=-10; x<10; x+=step) {
        const nx = x + step;
        const yA = p.a*x*x + p.b*x + p.c;
        const yB = p.a*nx*nx + p.b*nx + p.c;
        segs.push({
          start:[x,yA],
          end:[nx,yB],
          color:"green"
        });
      }
      break;
    }
    case "circle": {
      // approximate from x=(h-r)..(h+r)
      const step=0.3;
      for(let x= p.h - p.r; x<= p.h + p.r; x+= step) {
        const nx=x+step;
        // y= k ± sqrt(r^2-(x-h)^2)
        const rad1 = p.r**2 - (x - p.h)**2;
        const rad2 = p.r**2 - (nx - p.h)**2;
        if(rad1<0 || rad2<0) continue; // no real y
        const yP1 = p.k + Math.sqrt(rad1);
        const yN1 = p.k - Math.sqrt(rad1);
        const yP2 = p.k + Math.sqrt(rad2);
        const yN2 = p.k - Math.sqrt(rad2);
        // upper arc
        segs.push({ start:[x,yP1], end:[nx,yP2], color:"blue" });
        // lower arc
        segs.push({ start:[x,yN1], end:[nx,yN2], color:"blue" });
      }
      break;
    }
    case "exponential": {
      const step=0.3;
      for (let x=-10; x<10; x+=step) {
        const nx = x + step;
        const yA = p.a * Math.pow(p.b, x) + p.c;
        const yB = p.a * Math.pow(p.b, nx) + p.c;
        // skip if huge or invalid
        if (!Number.isFinite(yA) || !Number.isFinite(yB)) continue;
        segs.push({
          start:[x,yA],
          end:[nx,yB],
          color:"purple"
        });
      }
      break;
    }
    case "logarithm": {
      // only valid for x>0. We'll sample x from 0.1..10, say
      if (p.base<=0 || p.base===1) {
        // invalid base => no segments
        break;
      }
      const step=0.2;
      for(let x=0.1; x<10; x+=step) {
        const nx = x+step;
        const yA = Math.log(x)/Math.log(p.base); 
        const yB = Math.log(nx)/Math.log(p.base);
        if(!Number.isFinite(yA) || !Number.isFinite(yB)) continue;
        segs.push({
          start:[x,yA],
          end:[nx,yB],
          color:"brown"
        });
      }
      break;
    }
    default:
      // unknown => no segments
      break;
  }
  return segs;
}
